import useDeleteReturnReason from "@babackapp/private-api-client/client/use-delete-return-reason";
import useUpdateReturnReason from "@babackapp/private-api-client/client/use-update-return-reasons";
import type { ShopReasonsDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@babackapp/ui/components/dialog";
import { type PropsWithChildren, useCallback, useState } from "react";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import { useBoolean } from "@/hooks/use-boolean.ts";
import {
	type ReturnReasonFormValues,
	type ShopifyReason,
	useCurrentShopLocalesAvailables,
} from "./add-return-reason-dialog.tsx";
import ReturnReasonForm from "./return-reason-form.tsx";

type UpdateReturnReasonProps = {
	reason: ShopReasonsDto;
};

export default function UpdateReturnReasonDialog({
	reason,
	children,
}: PropsWithChildren<UpdateReturnReasonProps>) {
	const t = useTranslation();
	const open = useBoolean(false);
	const { data: languages } = useCurrentShopLocalesAvailables();

	const { data: shop } = useCurrentShopSuspense();

	const [error, setError] = useState<{ title?: string; error?: string } | null>(
		null,
	);

	const { mutateAsync: updateReturnReason } = useUpdateReturnReason(
		shop,
		reason.id!,
	);

	const { mutateAsync: removeReturnReason } = useDeleteReturnReason(shop);

	function getLanguages() {
		if (!languages) return null;
		return languages.map((lang) => ({
			lang,
			text: reason.values.find((v) => v.lang === lang)?.text ?? "",
		}));
	}

	const defaultValuesFields = getLanguages();

	const onSubmit = useCallback(
		(values: ReturnReasonFormValues) => {
			updateReturnReason({
				active: true,
				externalId: values.externalId || undefined,
				shopifyReason: values.shopifyReason,
				shopifyReasonNote:
					values.shopifyReason === "OTHER"
						? values.shopifyReasonNote || undefined
						: undefined,
				values: values.values.map((v) => ({ lang: v.lang, text: v.text })),
				rules: {
					approvalRequired: { available: values.approvalRequired },
					comment: {
						available: ["REQUIRED", "OPTIONAL"].includes(values.comments),
						required: values.comments === "REQUIRED",
					},
					photoRequired: {
						available: ["REQUIRED", "OPTIONAL"].includes(values.images),
						required: values.images === "REQUIRED",
					},
					skipReturnCost: { available: values.skipReturnCost },
					skipReshipping: { available: values.skipReshipping },
					highlighted: { available: values.highlighted },
					reservedForLoggedUser: { available: values.reservedForLoggedUser },
				},
			});
		},
		[updateReturnReason],
	);

	if (!defaultValuesFields) return null;

	return (
		<Dialog open={open.value} onOpenChange={open.setValue}>
			<DialogTrigger asChild>{children}</DialogTrigger>
			<DialogContent className="pt-10" aria-describedby={undefined}>
				<DialogHeader>
					<DialogTitle>{t("settings.reasons.popup.titleOnEdit")}</DialogTitle>
				</DialogHeader>
				<ReturnReasonForm
					onSubmit={onSubmit}
					onRemove={() => reason && removeReturnReason(reason.id!)}
					error={error}
					setError={setError}
					defaultValues={{
						externalId: reason.externalId ?? undefined,
						shopifyReason: reason.shopifyReason as ShopifyReason,
						shopifyReasonNote: reason.shopifyReasonNote ?? undefined,
						approvalRequired: reason.rules.approvalRequired.available,
						skipReturnCost: reason.rules.skipReturnCost.available,
						skipReshipping: reason.rules.skipReshipping.available,
						highlighted: reason.rules.highlighted.available,
						reservedForLoggedUser: reason.rules.reservedForLoggedUser.available,
						comments: reason.rules.comment.available
							? reason.rules.comment.required
								? "REQUIRED"
								: "OPTIONAL"
							: "NOT_AVAILABLE",
						images: reason.rules.photoRequired.available
							? reason.rules.photoRequired.required
								? "REQUIRED"
								: "OPTIONAL"
							: "NOT_AVAILABLE",
						values: defaultValuesFields,
					}}
				/>
			</DialogContent>
		</Dialog>
	);
}
